import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CircleAlert,
  CircleCheck,
  Check,
  FileText,
  Info,
  type LucideIcon,
  Save,
  Trash2,
  TriangleAlert,
  Upload
} from "lucide-react";
import { toast as sonnerToast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

type CasePrepToastPayload = {
  variant: "brand" | "neutral" | "error" | "success";
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Feather icon name (e.g. "FeatherCheck"), rendered with lucide. */
  icon?: string;
  actions?: React.ReactNode;
  durationMs?: number;
};

type CasePrepToastItem = CasePrepToastPayload & {
  id: number;
  isExiting?: boolean;
};

type CasePrepToastStatus = "success" | "error" | "warning" | "info" | "loading";

type CasePrepChakraToastPayload = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  status?: CasePrepToastStatus;
  duration?: number | null;
  id?: string | number;
  isClosable?: boolean;
  position?: string;
  [key: string]: unknown;
};

type CasePrepToastFunction = ((payload: CasePrepChakraToastPayload) => void) & {
  closeAll: () => void;
  isActive: (id?: string | number) => boolean;
};

const CASE_PREP_TOAST_EVENT = "caseblink:case-prep-toast";
const TOAST_EXIT_ANIMATION_MS = 180;

const LUCIDE_BY_FEATHER: Record<string, LucideIcon> = {
  FeatherCheck: Check,
  FeatherCheckCircle: CircleCheck,
  FeatherAlertCircle: CircleAlert,
  FeatherAlertTriangle: TriangleAlert,
  FeatherInfo: Info,
  FeatherFileText: FileText,
  FeatherSave: Save,
  FeatherTrash2: Trash2,
  FeatherUpload: Upload
};

const iconToneByVariant: Record<CasePrepToastPayload["variant"], string> = {
  success: "text-success",
  error: "text-danger",
  brand: "text-accent-seed",
  neutral: "text-fg-3"
};

const ToastIcon = ({
  icon = "FeatherInfo",
  variant
}: Pick<CasePrepToastPayload, "icon" | "variant">) => {
  const tone = iconToneByVariant[variant];
  if (icon === "FeatherLoader" || icon === "FeatherLoader2") {
    return <Spinner className={cn("size-4 shrink-0", tone)} />;
  }
  const Glyph = LUCIDE_BY_FEATHER[icon] ?? Info;
  return <Glyph className={cn("size-4 shrink-0", tone)} />;
};

const CasePrepToastCard = ({
  variant,
  icon,
  title,
  description,
  actions
}: Omit<CasePrepToastPayload, "durationMs">) => (
  <div className="flex w-80 items-center gap-3 rounded-lg bg-elevated px-3 py-2.5 shadow-popover ring-1 ring-line-3">
    <ToastIcon icon={icon} variant={variant} />
    <div className="flex min-w-0 grow flex-col items-start">
      {title ? (
        <span className="w-full text-base font-medium text-fg">{title}</span>
      ) : null}
      {description ? (
        <span className="w-full text-sm text-fg-3">{description}</span>
      ) : null}
    </div>
    {actions ? (
      <div className="flex items-center justify-end gap-1">{actions}</div>
    ) : null}
  </div>
);

const toastVisualsByStatus: Record<
  CasePrepToastStatus,
  Pick<CasePrepToastPayload, "variant" | "icon">
> = {
  success: { variant: "success", icon: "FeatherCheck" },
  error: { variant: "error", icon: "FeatherAlertCircle" },
  warning: { variant: "neutral", icon: "FeatherAlertTriangle" },
  info: { variant: "brand", icon: "FeatherInfo" },
  loading: { variant: "neutral", icon: "FeatherLoader" }
};

const defaultTitleByVariant: Record<CasePrepToastPayload["variant"], string> = {
  success: "Success",
  error: "Something went wrong",
  brand: "Update",
  neutral: "Notice"
};

// Guard against rendering raw data (e.g. an API error payload like
// `[{ loc, msg, type }]`) as a React child, which throws "Objects are not valid
// as a React child" and crashes the surrounding route.
const isUnsafeToastNode = (value: unknown): boolean =>
  !!value &&
  typeof value === "object" &&
  !React.isValidElement(value as object);

const stringifyUnsafeToastNode = (value: unknown): string => {
  const maybeMsg = (value as { msg?: unknown } | null)?.msg;
  if (typeof maybeMsg === "string" && maybeMsg.trim()) return maybeMsg;
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
};

const toSafeToastNode = (value: React.ReactNode): React.ReactNode => {
  if (Array.isArray(value)) {
    if (value.some((item) => isUnsafeToastNode(item))) {
      return value
        .map((item) =>
          isUnsafeToastNode(item) ? stringifyUnsafeToastNode(item) : item
        )
        .filter((item) => item != null && item !== "")
        .join("; ");
    }
    return value;
  }
  if (isUnsafeToastNode(value)) return stringifyUnsafeToastNode(value);
  return value;
};

const normalizeToastPayload = ({
  description,
  title,
  ...rest
}: CasePrepToastPayload): CasePrepToastPayload => {
  const safeTitle = toSafeToastNode(title);
  const safeDescription = toSafeToastNode(description);

  if (safeDescription || !safeTitle) {
    return { ...rest, title: safeTitle, description: safeDescription };
  }

  return {
    ...rest,
    title: defaultTitleByVariant[rest.variant],
    description: safeTitle
  };
};

export const showCasePrepToast = (payload: CasePrepToastPayload) => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  if (!document.querySelector('[data-case-prep-content-area="true"]')) {
    return false;
  }

  window.dispatchEvent(
    new CustomEvent<CasePrepToastPayload>(CASE_PREP_TOAST_EVENT, {
      detail: normalizeToastPayload(payload)
    })
  );
  return true;
};

export const showSubframeToast = (payload: CasePrepToastPayload) => {
  const { durationMs = 3500, ...rest } = normalizeToastPayload(payload);

  if (showCasePrepToast({ ...rest, durationMs })) return;

  const options = {
    description: rest.description,
    duration: durationMs,
    action: rest.actions,
    icon: rest.icon ? (
      <ToastIcon icon={rest.icon} variant={rest.variant} />
    ) : undefined
  };
  if (rest.variant === "success") sonnerToast.success(rest.title, options);
  else if (rest.variant === "error") sonnerToast.error(rest.title, options);
  else if (rest.variant === "brand") sonnerToast.info(rest.title, options);
  else sonnerToast(rest.title, options);
};

export const showCasePrepChakraToast = ({
  title,
  description,
  status = "info",
  duration = 3500
}: CasePrepChakraToastPayload) => {
  const visuals = toastVisualsByStatus[status] ?? toastVisualsByStatus.info;
  showSubframeToast({
    ...visuals,
    title,
    description,
    durationMs: duration ?? undefined
  });
};

export const useCasePrepToast = (): CasePrepToastFunction => {
  return useMemo(() => {
    const toast = (payload: CasePrepChakraToastPayload) => {
      showCasePrepChakraToast(payload);
    };
    toast.closeAll = () => {};
    toast.isActive = () => false;
    return toast as CasePrepToastFunction;
  }, []);
};

export const CasePrepToastViewport = () => {
  const [toasts, setToasts] = useState<CasePrepToastItem[]>([]);
  const timeoutIdsRef = useRef<number[]>([]);

  useEffect(() => {
    const handleToast = (event: Event) => {
      const { detail } = event as CustomEvent<CasePrepToastPayload>;
      const id = Date.now() + Math.random();
      const durationMs = detail.durationMs ?? 3500;

      setToasts((current) => [...current, { ...detail, id }]);
      const exitTimeoutId = window.setTimeout(() => {
        setToasts((current) =>
          current.map((toast) =>
            toast.id === id ? { ...toast, isExiting: true } : toast
          )
        );
      }, Math.max(durationMs - TOAST_EXIT_ANIMATION_MS, 0));
      const removeTimeoutId = window.setTimeout(() => {
        setToasts((current) => current.filter((toast) => toast.id !== id));
      }, durationMs);
      timeoutIdsRef.current.push(exitTimeoutId, removeTimeoutId);
    };

    window.addEventListener(CASE_PREP_TOAST_EVENT, handleToast);
    return () => {
      window.removeEventListener(CASE_PREP_TOAST_EVENT, handleToast);
      timeoutIdsRef.current.forEach((timeoutId) =>
        window.clearTimeout(timeoutId)
      );
      timeoutIdsRef.current = [];
    };
  }, []);

  if (!toasts.length) return null;

  return (
    <div
      className="pointer-events-none absolute left-0 right-0 z-9999 flex flex-col-reverse items-center gap-3 px-4"
      style={{ bottom: "calc(var(--case-prep-footer-offset, 0px) + 24px)" }}
    >
      <style>{`
        @keyframes casePrepToastIn {
          from {
            opacity: 0;
            transform: translateY(12px) scale(0.96);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes casePrepToastOut {
          from {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          to {
            opacity: 0;
            transform: translateY(10px) scale(0.98);
          }
        }

        .case-prep-toast {
          animation: casePrepToastIn 180ms cubic-bezier(0.22, 1, 0.36, 1) both;
          transform-origin: 50% 100%;
          will-change: opacity, transform;
        }

        .case-prep-toast[data-state="exiting"] {
          animation: casePrepToastOut ${TOAST_EXIT_ANIMATION_MS}ms cubic-bezier(0.64, 0, 0.78, 0) both;
        }

        @media (prefers-reduced-motion: reduce) {
          .case-prep-toast,
          .case-prep-toast[data-state="exiting"] {
            animation: none;
          }
        }
      `}</style>
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className="case-prep-toast pointer-events-auto"
          data-state={toast.isExiting ? "exiting" : "open"}
        >
          <CasePrepToastCard
            variant={toast.variant}
            icon={toast.icon}
            title={toast.title}
            description={toast.description}
            actions={toast.actions}
          />
        </div>
      ))}
    </div>
  );
};
